import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { toast } from "sonner";
import { Search, Download, Calendar, MapPin, Layers, IndianRupee, Loader2, X, Lock, ChevronRight } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/**
 * Vinci Master Ledger — Admin-only central accounting stream.
 *
 * Unifies paid orders + paid certificate fees into a single searchable
 * table with per-row GST breakdown (CGST/SGST/IGST) and a live server-side
 * totals card that stays in sync with every filter change.
 *
 * Endpoints
 * ---------
 * GET /api/admin/ledger             — paginated + filtered rows + totals
 * GET /api/admin/ledger/export.csv  — CSV of the filtered set (no pagination)
 */
export function AdminMasterLedger({ api }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [expanded, setExpanded] = useState({});   // dealId -> boolean

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [state, setState] = useState("");
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (fromDate) p.set("from_date", fromDate);
    if (toDate) p.set("to_date", toDate);
    if (state) p.set("state", state);
    if (category && category !== "all") p.set("category", category);
    if (brand) p.set("brand", brand);
    if (search) p.set("search", search);
    p.set("limit", String(pageSize));
    p.set("offset", String(page * pageSize));
    return p;
  }, [fromDate, toDate, state, category, brand, search, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ledger?${params.toString()}`);
      setRows(data.rows || []);
      setTotals(data.totals);
      setStates(data.states || []);
      setTotalCount(data.total_count || 0);
    } catch {
      toast.error("Could not load the ledger.");
    } finally {
      setLoading(false);
    }
  }, [api, params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [fromDate, toDate, state, category, brand, search]);

  const exportCsv = async () => {
    try {
      // Strip pagination for the CSV export.
      const p = new URLSearchParams(params);
      p.delete("limit"); p.delete("offset");
      const res = await api.get(`/admin/ledger/export.csv?${p.toString()}`,
                                 { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vinci-master-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ledger exported.");
    } catch {
      toast.error("Export failed.");
    }
  };

  const clearAll = () => {
    setFromDate(""); setToDate(""); setState(""); setCategory("all"); setBrand(""); setSearch("");
  };
  const hasFilters = fromDate || toDate || state || category !== "all" || brand || search;

  // ── Price-Lock deal grouping ──────────────────────────────────────────
  // Fold matching price_lock_booking + price_lock_settlement rows (same
  // ``deal_group_id``) into one expandable composite row that sums back
  // to the quotation total. Everything else stays a regular row.
  const grouped = useMemo(() => {
    const bag = new Map();
    const flat = [];
    for (const r of rows) {
      if (!r.deal_group_id) { flat.push({ kind: "row", row: r }); continue; }
      const existing = bag.get(r.deal_group_id);
      if (!existing) {
        const deal = { deal_group_id: r.deal_group_id, deal_group_ref: r.deal_group_ref,
                       children: [r], _order: flat.length };
        bag.set(r.deal_group_id, deal);
        flat.push({ kind: "deal", deal });
      } else {
        existing.children.push(r);
      }
    }
    // Order booking → settlement inside each deal
    for (const item of flat) {
      if (item.kind === "deal") {
        item.deal.children.sort((a, b) =>
          (a.deal_group_role === "booking" ? 0 : 1) -
          (b.deal_group_role === "booking" ? 0 : 1));
      }
    }
    return flat;
  }, [rows]);

  const renderRows = (_rows) => {
    // Deal grouping only kicks in when at least one price-lock invoice is
    // in view. Otherwise fall back to the original flat table.
    const anyDeal = grouped.some((i) => i.kind === "deal");
    if (!anyDeal) return _rows.map((r) => <FlatRow key={`${r.category}-${r.id}`} r={r} />);
    return grouped.map((item, idx) => {
      if (item.kind === "row") return <FlatRow key={`${item.row.category}-${item.row.id}`} r={item.row} />;
      const deal = item.deal;
      const total = deal.children.reduce((s, c) => s + (Number(c.total) || 0), 0);
      const subtotal = deal.children.reduce((s, c) => s + (Number(c.subtotal) || 0), 0);
      const gst = deal.children.reduce((s, c) => s + (Number(c.gst_total) || 0), 0);
      const payer = deal.children[0]?.payer_name || "—";
      const payerEmail = deal.children[0]?.payer_email || "";
      const state = deal.children[0]?.state || "";
      const date = deal.children[0]?.date;
      const dealKey = deal.deal_group_id;
      const isOpen = !!expanded[dealKey];
      return (
        <Fragment key={`deal-${dealKey}`}>
          <tr data-testid={`ledger-deal-${dealKey}`}
              className="border-t border-[#D8B23E]/25 hover:bg-[#D8B23E]/[0.04] cursor-pointer transition-colors bg-[#D8B23E]/[0.02]"
              onClick={() => setExpanded((e) => ({ ...e, [dealKey]: !e[dealKey] }))}>
            <td className="px-4 py-3 text-[#C9C9C2] whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                <ChevronRight size={12} className="transition-transform inline"
                              style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }} />
                {date ? new Date(date).toLocaleString("en-IN",
                  { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="font-mono text-xs text-[#F5F5F0] inline-flex items-center gap-1.5">
                <Lock size={11} className="text-[#D8B23E]" />
                {deal.deal_group_ref || dealKey.slice(0, 10)}
              </div>
              <div className="font-grotesk text-[9px] uppercase tracking-[0.18em] text-[#D8B23E] mt-0.5">
                Price-Locked Deal · {deal.children.length} invoice{deal.children.length === 1 ? "" : "s"}
              </div>
            </td>
            <td className="px-4 py-3">
              <div className="text-[#F5F5F0]">{payer}</div>
              {payerEmail && <div className="text-[10px] text-[#6f6f68]">{payerEmail}</div>}
            </td>
            <td className="px-4 py-3 text-[#C9C9C2]">{state || "—"}</td>
            <td className="px-4 py-3">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border text-[#D8B23E] border-[#D8B23E]/50">
                30/70 Split
              </span>
            </td>
            <td className="px-4 py-3 text-right text-[#C9C9C2]">{inr(subtotal)}</td>
            <td className="px-4 py-3 text-right text-[#F5F5F0]">{inr(gst)}</td>
            <td className="px-4 py-3 text-right font-serif text-base text-[#D8B23E]">{inr(total)}</td>
          </tr>
          {isOpen && deal.children.map((c) => (
            <tr key={`deal-child-${c.id}`} data-testid={`ledger-deal-child-${c.id}`}
                className="border-t border-white/[0.04] bg-black/40">
              <td className="pl-10 pr-4 py-2 text-[11px] text-[#8A8A82] whitespace-nowrap">
                {c.date ? new Date(c.date).toLocaleString("en-IN",
                  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
              </td>
              <td className="px-4 py-2">
                <div className="font-mono text-[11px] text-[#C9C9C2]">{c.reference}</div>
                {c.payment_id && <div className="font-mono text-[10px] text-[#6f6f68]">{c.payment_id}</div>}
              </td>
              <td className="px-4 py-2 text-[11px] text-[#8A8A82]" colSpan={2}>
                <span className="font-grotesk text-[9px] uppercase tracking-[0.18em]"
                      style={{ color: c.deal_group_role === "booking" ? "#D8B23E" : "#B87333" }}>
                  {c.deal_group_role === "booking" ? "Booking · 30%" : "Settlement · 70%"}
                </span>
              </td>
              <td className="px-4 py-2 text-[11px] text-[#8A8A82]">{c.brand}</td>
              <td className="px-4 py-2 text-right text-[11px] text-[#C9C9C2]">{inr(c.subtotal)}</td>
              <td className="px-4 py-2 text-right text-[11px] text-[#8A8A82]">{inr(c.gst_total)}</td>
              <td className="px-4 py-2 text-right font-mono text-[11px] text-[#F5F5F0]">{inr(c.total)}</td>
            </tr>
          ))}
        </Fragment>
      );
    });
  };

  return (
    <div data-testid="master-ledger" className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#B87333]">Compliance & Accounting</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2">Vinci Master Ledger</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-xl leading-relaxed">
            Every rupee that has landed with Vinci Inc. — orders and certificate fees — with a full GST breakdown per row. Searchable by date, state, brand, or free-text.
          </p>
        </div>
        <button data-testid="ledger-export-csv" onClick={exportCsv}
                className="rounded-full bg-[#B87333] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity inline-flex items-center gap-2">
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* Totals cards */}
      {totals && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="ledger-totals">
          <TotalCard label="Grand total" value={inr(totals.grand.total)} accent="#B87333"
                     sub={`${totals.grand.count} transaction${totals.grand.count === 1 ? "" : "s"}`} />
          <TotalCard label="GST collected" value={inr(totals.grand.gst)} accent="#2FB999"
                     sub={`CGST ${inr(totals.grand.cgst)} · SGST ${inr(totals.grand.sgst)} · IGST ${inr(totals.grand.igst)}`} />
          <TotalCard label="Orders" value={inr(totals.orders.total)} accent="#D8B23E"
                     sub={`${totals.orders.count} order${totals.orders.count === 1 ? "" : "s"}`} />
          <TotalCard label="Certificate fees" value={inr(totals.certificates.total)} accent="#C0392B"
                     sub={`${totals.certificates.count} certificate${totals.certificates.count === 1 ? "" : "s"} · ₹41,680 each`} />
        </div>
      )}

      {/* Filter bar */}
      <div className="rounded-sm border border-white/10 bg-black/30 p-5 grid grid-cols-1 md:grid-cols-6 gap-3">
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82] flex items-center gap-1"><Calendar size={11}/> From</span>
          <input data-testid="ledger-from-date" type="date" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82] flex items-center gap-1"><Calendar size={11}/> To</span>
          <input data-testid="ledger-to-date" type="date" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82] flex items-center gap-1"><MapPin size={11}/> State</span>
          <select data-testid="ledger-state" value={state} onChange={(e) => setState(e.target.value)}
                  className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]">
            <option value="">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82] flex items-center gap-1"><Layers size={11}/> Category</span>
          <select data-testid="ledger-category" value={category} onChange={(e) => setCategory(e.target.value)}
                  className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]">
            <option value="all">All revenue</option>
            <option value="order">Orders only</option>
            <option value="certificate">Certificate fees only</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82]">Brand</span>
          <input data-testid="ledger-brand" type="text" value={brand}
                 onChange={(e) => setBrand(e.target.value)}
                 placeholder="e.g. UltraTech"
                 className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="overline text-[#8A8A82] flex items-center gap-1"><Search size={11}/> Search</span>
          <input data-testid="ledger-search" type="text" value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="Payer / order id / payment id"
                 className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0]" />
        </label>
        {hasFilters && (
          <div className="md:col-span-6 flex items-center justify-between text-xs text-[#8A8A82]">
            <span>Showing filtered set — {totalCount} row{totalCount === 1 ? "" : "s"}.</span>
            <button data-testid="ledger-clear-filters" onClick={clearAll}
                    className="inline-flex items-center gap-1 font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#B87333] hover:text-[#D8B23E]">
              <X size={11}/> Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-sm border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="border-b border-white/10 bg-black/40">
              <tr className="text-left font-grotesk text-[10px] uppercase tracking-[0.18em] text-[#8A8A82]">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Reference · Order ID</th>
                <th className="px-4 py-3">Payer</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">Brand / Category</th>
                <th className="px-4 py-3 text-right">Subtotal</th>
                <th className="px-4 py-3 text-right">GST</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-[#8A8A82]" data-testid="ledger-loading">
                  <Loader2 size={20} className="animate-spin inline mr-2"/> Loading ledger…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-[#8A8A82]" data-testid="ledger-empty">
                  No transactions match the current filters.
                </td></tr>
              ) : renderRows(rows)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-black/20 text-xs text-[#8A8A82]">
            <span>Page {page + 1} of {Math.ceil(totalCount / pageSize)} · {totalCount} rows</span>
            <div className="flex gap-2">
              <button data-testid="ledger-prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="rounded-full border border-white/20 px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-40">
                Prev
              </button>
              <button data-testid="ledger-next-page" disabled={(page + 1) * pageSize >= totalCount}
                      onClick={() => setPage((p) => p + 1)}
                      className="rounded-full border border-white/20 px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.18em] disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FlatRow({ r }) {
  return (
    <tr data-testid={`ledger-row-${r.category}-${r.id}`}
        className="border-t border-white/[0.06] hover:bg-white/[0.02] transition-colors">
      <td className="px-4 py-3 text-[#C9C9C2] whitespace-nowrap">
        {r.date ? new Date(r.date).toLocaleString("en-IN",
          { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
      </td>
      <td className="px-4 py-3">
        <div className="font-mono text-xs text-[#F5F5F0]">{r.reference || r.order_id}</div>
        {r.payment_id && <div className="font-mono text-[10px] text-[#6f6f68] mt-0.5">{r.payment_id}</div>}
      </td>
      <td className="px-4 py-3">
        <div className="text-[#F5F5F0]">{r.payer_name}</div>
        {r.payer_email && <div className="text-[10px] text-[#6f6f68]">{r.payer_email}</div>}
      </td>
      <td className="px-4 py-3 text-[#C9C9C2]">{r.state || "—"}</td>
      <td className="px-4 py-3">
        <div className="text-[#F5F5F0]">{r.brand}</div>
        <span className="font-grotesk text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border"
              style={{ color: r.category === "certificate" ? "#C0392B" : "#B87333",
                       borderColor: r.category === "certificate" ? "#C0392B66" : "#B8733366" }}>
          {r.category === "certificate" ? "Cert fee" : r.category === "invoice" ? "Invoice" : "Order"}
        </span>
      </td>
      <td className="px-4 py-3 text-right text-[#C9C9C2]">{inr(r.subtotal)}</td>
      <td className="px-4 py-3 text-right">
        <div className="text-[#F5F5F0]">{inr(r.gst_total)}</div>
        {r.gst_total > 0 && (
          <div className="text-[9px] text-[#6f6f68] mt-0.5">
            {r.gst_mode === "intra"
              ? <>CGST {inr(r.gst_cgst)} · SGST {inr(r.gst_sgst)}</>
              : r.gst_mode === "inter"
                ? <>IGST {inr(r.gst_igst)}</>
                : "—"}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right font-serif text-base text-[#F5F5F0]">{inr(r.total)}</td>
    </tr>
  );
}

function TotalCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-sm border p-5"
         style={{ borderColor: `${accent}55`, background: `linear-gradient(180deg, ${accent}0d 0%, transparent 60%)` }}>
      <div className="overline flex items-center gap-1" style={{ color: accent }}>
        <IndianRupee size={11}/> {label}
      </div>
      <div className="font-serif text-2xl md:text-3xl mt-2 tracking-tight text-[#F5F5F0]">{value}</div>
      {sub && <div className="font-body text-xs text-[#8A8A82] mt-1 leading-relaxed">{sub}</div>}
    </div>
  );
}
